import { Injectable, inject } from '@angular/core';
import { usePreset } from '@primeuix/themes';
import { UserCustomization } from '@cosmos/shared';
import { StateService } from '@cosmos/angular/utils';
import { THEME_PRESETS, LOCAL_STORAGE_THEME_KEY, LOCAL_STORAGE_NAME_KEY } from '../custom-presets';
import { CosmosPreset } from '../app-preset';

/**
 * ThemeService
 * Handles dynamic theme switching based on user customization settings
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly stateService = inject(StateService);

  constructor() {
    // React to user changes (login/logout/profile update)
    this.stateService.currentUser$.subscribe((user) => {
      if (user) {
        if ((user.company as any)?.customization || user.customization) {
          this.applyCustomization(
            user.customization || (user.company as any)?.customization,
          );
        } else {
          // Logged in user has no customization, apply default
          this.applyDefaultTheme();
        }
      } else if (!localStorage.getItem(LOCAL_STORAGE_THEME_KEY)) {
        // No user and no persisted theme, ensure default is applied
        this.applyDefaultTheme();
      }
      // If no user but there is a persisted theme, we do nothing (keep current theme)
    });
  }

  /**
   * Apply user customization to the UI
   * This method is called during app initialization after user authentication
   *
   * @param customization - User customization settings from the backend
   */
  applyCustomization(
    customization: UserCustomization | null | undefined,
  ): void {
    // console.log('applyCustomization', customization);
    if (!customization || !customization.theme) {
      // No customization, ensure default theme is applied
      this.applyDefaultTheme();
      return;
    }

    const themeKey = customization.theme;
    const preset = THEME_PRESETS[themeKey];

    if (preset === undefined) {
      // console.warn(
      //   `[ThemeService] Unknown theme key: ${themeKey}. Falling back to default theme.`,
      // );
      this.applyDefaultTheme();
      return;
    }

    // Apply the custom preset
    if (preset === null) {
      // Explicitly set to default
      this.applyDefaultTheme();
    } else {
      usePreset(preset);
      localStorage.setItem(LOCAL_STORAGE_THEME_KEY, themeKey);
      // console.log(`[ThemeService] Applied custom theme: ${themeKey}`);
    }

    // Future: Handle logoUrl and companyName customization
    // This could involve updating a service that manages branding assets
    if (customization.logoUrl) {
      // console.log(
      //   `[ThemeService] Custom logo URL detected: ${customization.logoUrl}`,
      // );
      // TODO: Implement logo replacement logic
    }

    if (customization.companyName) {
      // console.log(
      //   `[ThemeService] Custom company name detected: ${customization.companyName}`,
      // );
      // TODO: Implement company name display logic
      localStorage.setItem(LOCAL_STORAGE_NAME_KEY, customization.companyName);
    }
  }

  /**
   * Apply the default Cosmos theme
   */
  private applyDefaultTheme(): void {
    usePreset(CosmosPreset);
    localStorage.removeItem(LOCAL_STORAGE_THEME_KEY);
    localStorage.removeItem(LOCAL_STORAGE_NAME_KEY);
    // console.log('[ThemeService] Applied default Cosmos theme');
  }

  /**
   * Manually switch to a different theme
   * This can be used for theme preview or testing purposes
   *
   * @param themeKey - The key of the theme to apply
   */
  switchTheme(themeKey: string): void {
    const customization: UserCustomization = { theme: themeKey };
    this.applyCustomization(customization);
  }
}
